import { lazy, memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import {
  addProductToCart,
  addShopifyEvent,
  getLiveAgentAvailability,
  getSessionData,
  request,
  useStompClient,
  useToken,
} from '@/modules/api';
import { useEvent } from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import {
  AddToCartItem,
  EventType,
  MessageReasonType,
  MessageSenderType,
  MessageType,
  ModelActionType,
  PageType,
  PopupState,
  SendToModelStatusType,
  WidgetPopupType,
} from '@/modules/models';
import {
  attachmentsAtom,
  bridgeAtom,
  chatIdAtom,
  isInitSentAtom,
  isLiveAgentAvailableAtom,
  isLiveAgentModeAtom,
  isLoadingMessageAtom,
  isMlAnsweredAtom,
  isOpenChatAtom,
  isResetChatAtom,
  isUnauthorizedAtom,
  isUserAnsweredAtom,
  isWidgetReadyAtom,
  messagesAtom,
  messageTimeoutAtom,
  mlVersionAtom,
  requestsQueueAtom,
  sessionAtom,
  sessionFinishTimeAtom,
  transferToBotAtom,
  transferToLiveAgentAtom,
  useAtom,
  useReset,
  useSetValue,
  useValue,
  widgetPopupsStatesAtom,
} from '@/store';
import { AppEvents, isInShopify, publishShopifyAnalytics } from '@/utils';
import { useMessageTimeout } from '../../data';

const Layout = lazy(() => import('../layout').then((module) => ({ default: module.Layout })));

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export const DataLayer = memo(
  () => {
    const [token] = useToken();

    const [isRequestsQueueFetching, setIsRequestsQueueFetching] = useState(false);
    const conversationStartedRef = useRef(false);

    const { toggleChat } = useBehaviour();

    const [isOpenChat, setIsOpenChat] = useAtom(isOpenChatAtom);
    const [requestsQueue, setRequestsQueue] = useAtom(requestsQueueAtom);
    const [messages, setMessages] = useAtom(messagesAtom);
    const [widgetPopupsStates, setWidgetPopupsStates] = useAtom(widgetPopupsStatesAtom);
    const [attachments, setAttachments] = useAtom(attachmentsAtom);

    const isUnauthorized = useValue(isUnauthorizedAtom);
    const chatId = useValue(chatIdAtom);
    const session = useValue(sessionAtom);
    const {
      onGetSuggestion,
      onSentSuggestion,
      onToggleChat,
      onResetChat,
      onMessageReady,
      isEmbedded,
      onWSSubscribed,
      onAddToCartFromChat,
      crossSaleMessage,
      shopifyAddToCartParams,
      isLiveAgentEnabled,
    } = useValue(bridgeAtom);
    const isLiveAgentMode = useValue(isLiveAgentModeAtom);

    const setIsMlAnswered = useSetValue(isMlAnsweredAtom);
    const setIsUserAnswered = useSetValue(isUserAnsweredAtom);
    const setResetChat = useSetValue(isResetChatAtom);
    const setMessageTimeout = useSetValue(messageTimeoutAtom);
    const setIsLoadingMessage = useSetValue(isLoadingMessageAtom);
    const setMlVersion = useSetValue(mlVersionAtom);
    const setIsLiveAgentMode = useSetValue(isLiveAgentModeAtom);
    const setIsLiveAgentAvailable = useSetValue(isLiveAgentAvailableAtom);
    const setTransferToLiveAgent = useSetValue(transferToLiveAgentAtom);
    const setTransferToBot = useSetValue(transferToBotAtom);
    const setIsInitSent = useSetValue(isInitSentAtom);

    const resetChatIdAtom = useReset(chatIdAtom);
    const resetSessionFinishTime = useReset(sessionFinishTimeAtom);

    const isCrossSaleSeen = widgetPopupsStates[WidgetPopupType.CROSS_SALE] === PopupState.SEEN;

    useMessageTimeout();

    const showCrossSale = useCallback(() => {
      if (isCrossSaleSeen) {
        return;
      }

      setWidgetPopupsStates((prevState) => ({
        ...prevState,
        [WidgetPopupType.CROSS_SALE]: PopupState.STARTED,
      }));
    }, [isCrossSaleSeen, setWidgetPopupsStates]);

    const resetChat = useCallback(() => {
      // Don't expire the session while a live agent is handling the conversation
      if (isLiveAgentMode) {
        return;
      }

      setResetChat(true);
      resetChatIdAtom();
      setIsOpenChat(false);
      resetSessionFinishTime();
      setMessages([]);
      setIsLiveAgentMode(false);

      // do not send a timeout message if chat is closed
      setMessageTimeout(-1);
      onResetChat?.();
    }, [
      resetChatIdAtom,
      setIsOpenChat,
      resetSessionFinishTime,
      setResetChat,
      setMessages,
      setIsLiveAgentMode,
      setMessageTimeout,
      onResetChat,
      isLiveAgentMode,
    ]);

    useEvent({ name: AppEvents.RESET_CHAT, handler: resetChat });
    useEvent({ name: AppEvents.SHOW_CROSS_SALE, handler: showCrossSale });

    const updateChatHistory = useCallback(
      (newMessage: MessageType) => {
        if (newMessage?.action === ModelActionType.NONE) {
          return;
        }

        setMessages((prev) => {
          const messageExists = prev.some(
            (message: MessageType) =>
              (message.id != null && message.id === newMessage.id && message.role === newMessage.role) ||
              (newMessage.clientCorrelationId != null && message.clientCorrelationId === newMessage.clientCorrelationId)
          );

          if (messageExists) {
            return prev.map((message) =>
              (message.id != null && message.id === newMessage.id) ||
              (newMessage.clientCorrelationId != null && message.clientCorrelationId === newMessage.clientCorrelationId)
                ? newMessage
                : message
            );
          } else {
            return [...prev, newMessage];
          }
        });
      },
      [setMessages]
    );

    const handleAddToCartFromMessage = useCallback(
      async (items: AddToCartItem[]) => {
        const userSession = await getSessionData();

        let lastProduct = null;

        for (const item of items) {
          try {
            lastProduct = await addProductToCart({
              handle: item.handle,
              variantId: item.variant_id || '',
              quantity: item.quantity,
              lineItemProperties: item.line_item_properties,
              sellingPlan: item.subscription_plan_id ?? undefined,
              params: shopifyAddToCartParams,
            });
          } catch (error) {
            console.error('Saywhatt:handleAddToCartFromMessage - addProductToCart failed:', {
              handle: item.handle,
              variantId: item.variant_id,
              error,
            });
          }

          try {
            await addShopifyEvent({
              userSession,
              type: EventType.ADD_TO_CART_BY_USER_REQUEST,
              handle: item.handle,
              quantity: item.quantity,
            });
            publishShopifyAnalytics('saywhatt_chat_add_to_cart_by_user_request', {
              handle: item.handle,
              quantity: item.quantity,
            });
          } catch (error) {
            console.error('Saywhatt:handleAddToCartFromMessage - addShopifyEvent failed:', {
              handle: item.handle,
              error,
            });
          }
        }

        if (lastProduct && onAddToCartFromChat) {
          onAddToCartFromChat(lastProduct, null as any);
        }

        if (crossSaleMessage) {
          showCrossSale();
        }
      },
      [onAddToCartFromChat, shopifyAddToCartParams]
    );

    const onMessageReceived = useCallback(
      (newMessage: MessageType) => {
        if (
          newMessage.reason === MessageReasonType.INITIALIZATION &&
          newMessage.role === MessageSenderType.USER &&
          newMessage.status === SendToModelStatusType.SUCCESS
        ) {
          setIsInitSent(true);
          onMessageReady?.();
        }
        if (
          newMessage.reason === MessageReasonType.INITIALIZATION &&
          newMessage.page_type === PageType.ADD_TO_CART_SUGGESTION &&
          newMessage.status === SendToModelStatusType.SUCCESS &&
          newMessage.role === MessageSenderType.USER
        ) {
          onSentSuggestion?.(newMessage.id as number);
        }

        if (!chatId || !newMessage.chatId || newMessage.chatId !== chatId) {
          return;
        }

        if (newMessage.addToCart?.length && newMessage.action === ModelActionType.ADD_TO_CART) {
          void handleAddToCartFromMessage(newMessage.addToCart);
        }

        if (!newMessage.content && !newMessage.contentNew && !newMessage.attachments?.length) {
          return;
        }

        // AgentMessageDto: real-time message from a live agent
        if (newMessage.role === MessageSenderType.LIVE_AGENT) {
          setIsLoadingMessage(false);
          updateChatHistory(newMessage);
          return;
        }

        // SystemMessageDto: system event (session ended / transfer denied)
        if (newMessage.role === MessageSenderType.SYSTEM) {
          if (newMessage.content?.toLowerCase().includes('conversation with the live agent has ended')) {
            setIsLiveAgentMode(false);
          }
          setIsLoadingMessage(false);
          updateChatHistory(newMessage);
          return;
        }

        if (
          newMessage.reason === MessageReasonType.MODEL_RESPONSE &&
          newMessage.pageType === PageType.ADD_TO_CART_SUGGESTION &&
          newMessage.role === MessageSenderType.ASSISTANT
        ) {
          onGetSuggestion?.({
            replyMessageId: newMessage.replyMessageId as number,
            content: newMessage.contentNew,
          });
        }

        if (newMessage.mlVersion) {
          setMlVersion(newMessage.mlVersion);
        }

        switch (newMessage.reason) {
          case MessageReasonType.USER_INPUT: {
            setIsLoadingMessage(true);
            updateChatHistory(newMessage);
            return;
          }
          case MessageReasonType.SYSTEM_MESSAGE: {
            // LiveAgentStartedDto transformed in onTopic (session started / connection message)
            setIsLoadingMessage(false);
            updateChatHistory(newMessage);
            return;
          }
          case MessageReasonType.MODEL_RESPONSE: {
            if (newMessage.action === ModelActionType.HIBERNATE) {
              toggleChat();
              return;
            }

            if (!isEmbedded || (isEmbedded && newMessage.action !== ModelActionType.NONE)) {
              setIsLoadingMessage(false);
            }

            const newMessageTimeout = newMessage.timeout || -1;

            setMessageTimeout(newMessageTimeout);
            updateChatHistory(newMessage);
          }
        }
      },
      [
        chatId,
        isEmbedded,
        setIsLoadingMessage,
        setIsLiveAgentMode,
        setMessageTimeout,
        toggleChat,
        updateChatHistory,
        setMlVersion,
        onGetSuggestion,
        onSentSuggestion,
        onMessageReady,
        handleAddToCartFromMessage,
      ]
    );

    const onHistoryReceived = useCallback(
      (messages: MessageType[]) => {
        const lastMessage = messages[messages.length - 1];
        const messageWithMlVersion = messages.findLast((mess) => mess.mlVersion);
        const mlVersion = messageWithMlVersion?.mlVersion;
        if (mlVersion) {
          setMlVersion(mlVersion);
        }

        if (
          lastMessage?.reason === MessageReasonType.MODEL_RESPONSE &&
          (lastMessage.action === ModelActionType.SAY ||
            lastMessage.action === ModelActionType.ADD_TO_CART ||
            lastMessage.action === ModelActionType.NONE)
        ) {
          setMessageTimeout(lastMessage.timeout || -1);
          setIsLoadingMessage(false);
        } else if (
          lastMessage?.role === MessageSenderType.LIVE_AGENT ||
          lastMessage?.role === MessageSenderType.SYSTEM
        ) {
          setIsLoadingMessage(false);
        }

        setMessages(
          messages
            .filter(
              (m) =>
                (m.action === null ||
                  m.action === ModelActionType.SAY ||
                  m.action === ModelActionType.ADD_TO_CART ||
                  m.role === MessageSenderType.LIVE_AGENT ||
                  m.role === MessageSenderType.SYSTEM) &&
                (m.content != null || m.contentNew != null || m.attachments?.length)
            )
            .sort((a, b) => (a.clientTimestamp ?? 0) - (b.clientTimestamp ?? 0))
        );
      },
      [setMessages, setIsLoadingMessage, setMessageTimeout, setMlVersion]
    );

    const { sendMessage, initConversation, isSubscribed, transferToLiveAgent, transferToBot } = useStompClient({
      onMessageReceived,
      onHistoryReceived,
      onReset: resetChat,
    });

    const setIsWidgetReady = useSetValue(isWidgetReadyAtom);
    useEffect(() => {
      setIsWidgetReady(true);
    }, []);

    const onSendMessage = useCallback(
      async (message: MessageType) => {
        if (!isLiveAgentMode) {
          setIsLoadingMessage(true);
        }
        if (!conversationStartedRef.current) {
          conversationStartedRef.current = true;
          publishShopifyAnalytics('saywhatt_chat_conversation_started');
        }

        const messageToSend = attachments.length > 0 ? { ...message, hasAttachments: true, attachments } : message;

        const response = await sendMessage(messageToSend);
        setAttachments([]);

        if (response?.error) {
          setIsLoadingMessage(false);
        }

        return response;
      },

      [sendMessage, isLiveAgentMode, attachments, setAttachments]
    );

    const hasSession = Boolean(session);

    const hasCheckedAvailabilityRef = useRef(false);
    useEffect(() => {
      if (!token || hasCheckedAvailabilityRef.current || !isLiveAgentEnabled) {
        return;
      }
      hasCheckedAvailabilityRef.current = true;
      getLiveAgentAvailability()
        .then(({ is_available }) => setIsLiveAgentAvailable(is_available))
        .catch(() => {}); // leave null (hidden) on network/auth errors
    }, [token, isLiveAgentEnabled]);

    useEffect(() => {
      setTransferToLiveAgent(() => transferToLiveAgent);
    }, [transferToLiveAgent]);

    useEffect(() => {
      setTransferToBot(() => transferToBot);
    }, [transferToBot]);

    useEffect(() => {
      if (!isOpenChat) {
        return;
      }

      if (!isSubscribed && !isEmbedded && !isLiveAgentMode) {
        setIsLoadingMessage(true);
      }

      if (isSubscribed && hasSession && !isLiveAgentMode) {
        initConversation();
      }

      if (isSubscribed && isLiveAgentMode) {
        setIsLoadingMessage(false);
      }
    }, [isSubscribed, isOpenChat, hasSession, isEmbedded, isLiveAgentMode]);

    useEffect(() => {
      if (isSubscribed) {
        onWSSubscribed?.();
      }
    }, [onWSSubscribed, isSubscribed]);

    useEffect(() => {
      const mlMessages = messages.filter(
        ({ reason, role }) => reason === MessageReasonType.MODEL_RESPONSE && role === MessageSenderType.ASSISTANT
      );
      const userMessages = messages.filter(
        ({ reason, role }) => reason === MessageReasonType.USER_INPUT && role === MessageSenderType.USER
      );
      setIsMlAnswered(mlMessages.length >= 2);
      setIsUserAnswered(userMessages.length > 0);
    }, [messages, setIsMlAnswered, setIsUserAnswered]);

    useEffect(() => {
      if (!isRequestsQueueFetching && !isUnauthorized && requestsQueue.length > 0 && token) {
        setIsRequestsQueueFetching(true);
        (async () => {
          for (const req of requestsQueue) {
            try {
              await request.request(req.url, {
                method: req.method,
                body: req.body ? JSON.parse(req.body) : undefined,
                headers: req.headers,
              });
            } catch (err) {
              console.error('Failed to retry request:', req.url, err);
            }
          }

          setRequestsQueue([]);
          setIsRequestsQueueFetching(false);
        })();
      }
    }, [isRequestsQueueFetching, isUnauthorized, token, requestsQueue, setRequestsQueue]);

    // This effect is used for analytics. By userId we can associate the user and his cart.
    const userIdFromToken = token ? parseJwt(token).sub : null;
    useEffect(() => {
      if (!isInShopify()) {
        // we're not in scope of Shopify theme - do nothing
        return;
      }
      const shopifyRootUrl = window.Shopify?.routes?.root ?? '/';
      fetch(shopifyRootUrl + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            user_id: `${userIdFromToken}`,
          },
        }),
      }).catch((error) => {
        console.error('Error sending user id:', error);
      });
    }, [userIdFromToken]);

    useEffect(() => {
      onToggleChat?.(isOpenChat);
    }, [isOpenChat, onToggleChat]);

    if (!token) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <Layout onSendMessage={onSendMessage} />
      </Suspense>
    );
  },
  () => true
);
